/**
 * PrivacyDataTable component for marketing pages
 * Explicit privacy information display table showing what is and isn't stored
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface PrivacyDataRow {
  /**
   * Category name
   * @example "Email content", "Metadata", "Retention"
   */
  category: string;
  /**
   * What IS stored (use "—" for nothing stored)
   * @example "Subject lines, message IDs (encrypted)"
   */
  stored: string;
  /**
   * What is NOT stored (use "—" for everything stored)
   * @example "Body, attachments"
   */
  notStored: string;
}

export interface PrivacyDataTableProps {
  /**
   * Array of privacy data rows
   */
  data: PrivacyDataRow[];
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * PrivacyDataTable component
 *
 * Displays a clean, scannable table showing what data Email Ferret stores
 * and what it doesn't store. Used in privacy sections to build trust.
 *
 * @param data - Array of privacy data rows
 * @param className - Optional additional CSS classes
 *
 * @example
 * ```tsx
 * <PrivacyDataTable
 *   data={[
 *     {
 *       category: "Email content",
 *       stored: "—",
 *       notStored: "Body, attachments"
 *     },
 *     {
 *       category: "Metadata",
 *       stored: "Subject lines, message IDs (encrypted)",
 *       notStored: "—"
 *     }
 *   ]}
 * />
 * ```
 */
export function PrivacyDataTable({
  data,
  className = '',
}: PrivacyDataTableProps): React.ReactElement {
  return (
    <div className={cn(className)}>
      {/* Mobile: stacked card layout */}
      <div
        className="sm:hidden space-y-3"
        role="list"
        aria-label="Privacy data storage information"
      >
        {data.map((row) => (
          <div
            key={row.category}
            role="listitem"
            className="rounded-lg border border-border/40 p-4"
          >
            <p className="font-semibold text-foreground text-sm mb-2">{row.category}</p>
            <div className="space-y-1 text-sm">
              {row.stored !== '—' && (
                <p>
                  <span className="text-muted-foreground">Stored: </span>
                  <span className="text-foreground/90">{row.stored}</span>
                </p>
              )}
              {row.notStored !== '—' && (
                <p>
                  <span className="text-muted-foreground">Not stored: </span>
                  <span className="text-foreground/90">{row.notStored}</span>
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table layout */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse" aria-label="Privacy data storage information">
          <thead>
            <tr className="border-b-2 border-border/60">
              <th
                scope="col"
                className="text-left py-4 px-6 font-semibold text-foreground text-base"
                aria-label="Category"
              >
                Category
              </th>
              <th
                scope="col"
                className="text-center py-4 px-6 font-semibold text-foreground text-base"
                aria-label="Stored"
              >
                Stored
              </th>
              <th
                scope="col"
                className="text-right py-4 px-6 font-semibold text-foreground text-base"
                aria-label="Not stored"
              >
                Not stored
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={row.category}
                className="border-b border-border/40 hover:bg-muted/40 transition-colors duration-200"
              >
                <td className="py-4 px-6 font-semibold text-foreground">{row.category}</td>
                <td className="py-4 px-6 text-center text-muted-foreground">
                  {row.stored === '—' ? (
                    <span className="text-muted-foreground/50 italic text-sm">—</span>
                  ) : (
                    <span className="text-foreground/90">{row.stored}</span>
                  )}
                </td>
                <td className="py-4 px-6 text-right text-muted-foreground">
                  {row.notStored === '—' ? (
                    <span className="text-muted-foreground/50 italic text-sm">—</span>
                  ) : (
                    <span className="text-foreground/90">{row.notStored}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
